import React, { useState } from 'react';
import { Send, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';

const SupportTicketForm = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        category: '',
        priority: 'medium',
        description: ''
    });
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!formData.name || !formData.email || !formData.subject || !formData.category || !formData.description) {
            setError('Veuillez remplir tous les champs obligatoires');
            return;
        }

        // Simuler l'envoi (dans une vraie app, ceci ferait un appel API)
        try {
            // Simuler un délai
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Ici, vous intégreriez votre API de support
            console.log('Ticket soumis:', formData);
            
            setSubmitted(true);
            setFormData({
                name: '',
                email: '',
                subject: '',
                category: '',
                priority: 'medium',
                description: ''
            });
        } catch (err) {
            setError('Une erreur est survenue. Veuillez réessayer.');
        }
    };

    if (submitted) {
        return (
            <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
                <CheckCircle className="text-green-600 text-6xl mx-auto mb-4" />
                <h3 className="text-xl font-bold text-green-900 mb-2">Ticket soumis avec succès !</h3>
                <p className="text-green-800 mb-4">
                    Votre demande de support a été enregistrée. Notre équipe vous répondra dans les plus brefs délais.
                </p>
                <p className="text-sm text-green-700 mb-4">
                    Un email de confirmation a été envoyé à {formData.email || 'votre adresse email'}.
                </p>
                <button
                    onClick={() => setSubmitted(false)}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-all"
                >
                    Soumettre un autre ticket
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
                    <ErrorIcon className="text-red-600" />
                    <span className="text-red-900">{error}</span>
                </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
                <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                        Nom complet <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        placeholder="Jean Dupont"
                    />
                </div>

                <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                        Email <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        placeholder="jean@exemple.com"
                    />
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                <div>
                    <label htmlFor="category" className="block text-sm font-semibold text-gray-900 mb-2">
                        Catégorie <span className="text-red-500">*</span>
                    </label>
                    <select
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    >
                        <option value="">Sélectionner une catégorie</option>
                        <option value="authentication">Authentification / Clés API</option>
                        <option value="payment">Paiements</option>
                        <option value="webhooks">Webhooks</option>
                        <option value="integration">Intégration</option>
                        <option value="billing">Facturation</option>
                        <option value="technical">Problème technique</option>
                        <option value="other">Autre</option>
                    </select>
                </div>

                <div>
                    <label htmlFor="priority" className="block text-sm font-semibold text-gray-900 mb-2">
                        Priorité
                    </label>
                    <select
                        id="priority"
                        name="priority"
                        value={formData.priority}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    >
                        <option value="low">Basse</option>
                        <option value="medium">Moyenne</option>
                        <option value="high">Haute</option>
                        <option value="urgent">Urgente</option>
                    </select>
                </div>
            </div>

            <div>
                <label htmlFor="subject" className="block text-sm font-semibold text-gray-900 mb-2">
                    Sujet <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="Résumé de votre problème"
                />
            </div>

            <div>
                <label htmlFor="description" className="block text-sm font-semibold text-gray-900 mb-2">
                    Description détaillée <span className="text-red-500">*</span>
                </label>
                <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={8}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="Décrivez votre problème en détail, incluez des messages d'erreur, des logs, ou des étapes pour reproduire le problème..."
                />
                <p className="mt-2 text-sm text-gray-600">
                    Incluez autant de détails que possible : messages d'erreur, logs, étapes de reproduction, etc.
                </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-900 text-sm">
                    <strong>Note :</strong> Pour les problèmes urgents affectant la production, contactez-nous directement par téléphone ou email.
                </p>
            </div>

            <button
                type="submit"
                className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-lg font-medium hover:from-amber-600 hover:to-yellow-700 transition-all flex items-center justify-center space-x-2"
            >
                <Send />
                <span>Soumettre le ticket</span>
            </button>
        </form>
    );
};

export default SupportTicketForm;